/**
 * TemplateStore — the templates feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY templates module that talks to the API, and
 * it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<Template>('templates')` + `api.identityProvider`). It never
 * imports ApiClient or calls fetch. React hooks call this store; pages call the
 * hook.
 *
 * Persistence model: each template VERSION is its own document with id
 * `${name}@${version}`. Queryable metadata (name, version, description,
 * createdBy, updatedAt, fieldCount, deleted) is mirrored onto the DDB pointer
 * row's index attributes so the list view renders without hydrating S3 bodies.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; {@link listSummaries}
 * filters deleted rows out.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { Template, TemplateSummary } from './types';
import { templateDocId } from './templateModel';

export const TEMPLATES_COLLECTION = 'templates';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  name: 'name',
  version: 'version',
  description: 'description',
  createdBy: 'createdBy',
  updatedAt: 'updatedAt',
  fieldCount: 'fieldCount',
  deleted: 'deleted',
} as const;

export class TemplateStore {
  private readonly docs: DocumentStore<Template>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Template>(TEMPLATES_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(t: Template): StringMap {
    return {
      [ROW.name]: t.name,
      [ROW.version]: String(t.version),
      [ROW.description]: t.description ?? '',
      [ROW.createdBy]: t.createdBy ?? '',
      [ROW.updatedAt]: t.updatedAt ?? '',
      [ROW.fieldCount]: String(t.fields.length),
      [ROW.deleted]: t.deleted ? 'true' : 'false',
    };
  }

  /** Projects a raw pointer row into a lightweight summary. */
  private toSummary(row: StringMap): TemplateSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      name: row[ROW.name] ?? '',
      version: Number(row[ROW.version] ?? '0') || 0,
      description: row[ROW.description] ?? '',
      createdBy: row[ROW.createdBy] ?? '',
      updatedAt: row[ROW.updatedAt] ?? '',
      fieldCount: Number(row[ROW.fieldCount] ?? '0') || 0,
    };
  }

  /**
   * Lists non-deleted template summaries across all versions. Filters to this
   * collection defensively (the underlying list is not collection-scoped).
   */
  async listSummaries(): Promise<TemplateSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TEMPLATES_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single template version document by id. */
  async get(id: string): Promise<Template | undefined> {
    return this.docs.get(id);
  }

  /**
   * Computes the next version number for a name from existing (including
   * deleted) rows, so versions never collide even after deletions.
   */
  async nextVersion(name: string): Promise<number> {
    const rows = await this.docs.list();
    const max = rows
      .filter(
        (r) => r[ROW.collection] === TEMPLATES_COLLECTION && r[ROW.name] === name,
      )
      .reduce((acc, r) => Math.max(acc, Number(r[ROW.version] ?? '0') || 0), 0);
    return max + 1;
  }

  /**
   * Creates a new template version. Assigns the next available version for the
   * name, stamps createdBy (from identity) + updatedAt, and returns the saved
   * document.
   */
  async create(
    draft: Pick<Template, 'name' | 'description' | 'fields' | 'outputs'>,
  ): Promise<Template> {
    const identity = await this.api.identityProvider.getIdentity();
    const version = await this.nextVersion(draft.name);
    return this.persistNew(draft, version, identity.alias);
  }

  /**
   * Saves the current editor content as a brand-new version of an existing
   * template name (bumps version, new document id, retains history).
   */
  async saveNewVersion(
    draft: Pick<Template, 'name' | 'description' | 'fields' | 'outputs'>,
  ): Promise<Template> {
    return this.create(draft);
  }

  /** Shared new-document writer. */
  private async persistNew(
    draft: Pick<Template, 'name' | 'description' | 'fields' | 'outputs'>,
    version: number,
    createdBy: string,
  ): Promise<Template> {
    const template: Template = {
      id: templateDocId(draft.name, version),
      name: draft.name,
      version,
      description: draft.description ?? '',
      createdBy,
      updatedAt: new Date().toISOString(),
      fields: draft.fields,
      outputs: draft.outputs ?? [],
      deleted: false,
    };
    await this.docs.save(template.id, template, this.indexAttributes(template));
    return template;
  }

  /**
   * Updates an existing version document in place (same id/version). Refreshes
   * updatedAt and re-mirrors index attributes.
   */
  async update(template: Template): Promise<Template> {
    const updated: Template = {
      ...template,
      updatedAt: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Soft-deletes a version document: fetches it, flags it deleted, and updates
   * both the S3 body and the pointer row so list() filters it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Template = {
      ...existing,
      deleted: true,
      updatedAt: new Date().toISOString(),
    };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }
}
